import React, { useState, useEffect } from 'react';
import { useData } from '../context/DataContext';
import './Products.css';
import { UPLOAD_URL } from '../config/api';

const Products = () => {
    const { products, addProduct, updateProduct, deleteProduct, productsLoading, fetchProducts } = useData();
    
    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        variants: [{ weight: '', unit: 'gms', price: '', quantity: '' }]
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const itemsPerPage = 10;
    const totalPages = Math.ceil(products.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = products.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleVariantChange = (index, field, value) => {
        const newVariants = [...formData.variants];
        newVariants[index][field] = value;
        setFormData({
            ...formData,
            variants: newVariants
        });
    };

    const addVariant = () => {
        setFormData({
            ...formData,
            variants: [...formData.variants, { weight: '', unit: 'gms', price: '', quantity: '' }]
        });
    };

    const removeVariant = (index) => {
        if (formData.variants.length > 1) {
            const newVariants = formData.variants.filter((_, i) => i !== index);
            setFormData({
                ...formData,
                variants: newVariants
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                alert('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }

            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validation
        if (!formData.name.trim()) {
            setError('Product name is required');
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (formData.name.trim().length < 3) {
            setError('Product name must be at least 3 characters');
            setTimeout(() => setError(''), 3000);
            return;
        }

        // Check for duplicate product name
        const isDuplicate = products.some(p => {
            const existingName = p.productName?.toLowerCase().trim();
            const newName = formData.name.toLowerCase().trim();
            // If editing, exclude the current product from duplicate check
            if (editingProduct) {
                const editingId = editingProduct._id || editingProduct.id;
                const productId = p._id || p.id;
                return existingName === newName && productId !== editingId;
            }
            return existingName === newName;
        });

        if (isDuplicate) {
            setError('A product with this name already exists');
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (!formData.description.trim()) {
            setError('Description is required');
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (formData.description.trim().length < 10) {
            setError('Description must be at least 10 characters');
            setTimeout(() => setError(''), 3000);
            return;
        }



        // Validate variants
        if (formData.variants.length === 0) {
            setError('At least one variant is required');
            setTimeout(() => setError(''), 3000);
            return;
        }

        for (let i = 0; i < formData.variants.length; i++) {
            const variant = formData.variants[i];
            
            if (!variant.weight || parseFloat(variant.weight) <= 0) {
                setError(`Variant ${i + 1}: Weight must be greater than 0`);
                setTimeout(() => setError(''), 3000);
                return;
            }

            if (!variant.price || parseFloat(variant.price) <= 0) {
                setError(`Variant ${i + 1}: Price must be greater than 0`);
                setTimeout(() => setError(''), 3000);
                return;
            }

            if (!variant.quantity || parseInt(variant.quantity) < 0) {
                setError(`Variant ${i + 1}: Quantity must be 0 or greater`);
                setTimeout(() => setError(''), 3000);
                return;
            }
        }
        
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name.trim());
        formDataToSend.append('description', formData.description.trim());
        formDataToSend.append('variants', JSON.stringify(formData.variants.map(v => ({
            weight: parseFloat(v.weight),
            unit: v.unit,
            price: parseFloat(v.price),
            quantity: parseInt(v.quantity)
        }))));
        
        if (imageFile) {
            formDataToSend.append('image', imageFile);
        }

        let result;
        if (editingProduct) {
            const productId = editingProduct._id || editingProduct.id;
            result = await updateProduct(productId, formDataToSend);
        } else {
            result = await addProduct(formDataToSend);
        }

        if (result.success) {
            resetForm();
        } else {
            setError(result.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.productName,
            description: product.description,
            variants: product.variants && product.variants.length > 0 
                ? product.variants.map(v => ({
                    weight: v.weight.toString(),
                    unit: v.unit || 'gms',
                    price: v.price.toString(),
                    quantity: v.quantity.toString()
                }))
                : [{ weight: '', unit: 'gms', price: '', quantity: '' }]
        });
        setImagePreview(`${UPLOAD_URL}${product.productImage?.replace(/\\/g, '/')}`);
        setImageFile(null);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            const result = await deleteProduct(id);
            if (!result.success) {
                setError(result.message);
                setTimeout(() => setError(''), 3000);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            variants: [{ weight: '', unit: 'gms', price: '', quantity: '' }]
        });
        setImageFile(null);
        setImagePreview('');
        setEditingProduct(null);
        setShowModal(false);
    };

    return (
        <div className="products-page">
            <div className="page-header">
                <h1 className="page-title">Products</h1>
                <button className="btn-add" onClick={() => setShowModal(true)}>
                    + Add Product
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>{error}</div>}

            {productsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading products...</div>
            ) : (
            <div className="products-table-container">
                <div className="products-table-wrapper">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Product Name</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentProducts.map((product) => {
                                const productId = product._id || product.id;
                                // Check if any variant has stock
                                const hasStock = product.variants && product.variants.some(v => v.quantity > 0);
                                const totalStock = product.variants ? product.variants.reduce((sum, v) => sum + (v.quantity || 0), 0) : 0;
                                
                                return (
                                <tr key={productId}>
                                    <td>
                                        <img 
                                            src={product?.productImage ? `${UPLOAD_URL}${product?.productImage?.replace(/\\/g, '/')}` : '/item-image.png'} 
                                            alt={product.productName} 
                                            className="product-image" 
                                        />
                                    </td>
                                    <td>{product.productName}</td>
                                    <td className="description-cell">{product.description}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button className="btn-edit" onClick={() => handleEdit(product)}>
                                                ✏️ Edit
                                            </button>
                                            <button className="btn-delete" onClick={() => handleDelete(productId)}>
                                                🗑️ Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ← Previous
                        </button>
                        <div className="pagination-numbers">
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index + 1}
                                    className={`pagination-number ${currentPage === index + 1 ? 'active' : ''}`}
                                    onClick={() => goToPage(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                        <button
                            className="pagination-btn"
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                            <button className="modal-close" onClick={resetForm}>×</button>
                        </div>
                        <form onSubmit={handleSubmit} className="product-form">
                            {error && <div className="error-message" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>{error}</div>}
                            <div className="form-group">
                                <label>Product Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    minLength="3"
                                    className="form-input"
                                    placeholder="Enter product name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Product Image (Optional)</label>
                                <input
                                    type="file"
                                    name="image"
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    className="form-input"
                                />
                                {imagePreview && (
                                    <div className="image-preview">
                                        <img src={imagePreview} alt="Preview" />
                                    </div>
                                )}
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                    minLength="10"
                                    className="form-textarea"
                                    rows="3"
                                    placeholder="Enter product description (min 10 characters)"
                                />
                            </div>
                            
                            <div className="form-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ margin: 0 }}>Product Variants (Weight, Price, Quantity)</label>
                                    <button type="button" onClick={addVariant} className="btn-add" style={{ padding: '5px 15px', fontSize: '14px' }}>
                                        + Add Variant
                                    </button>
                                </div>
                                {formData.variants.map((variant, index) => (
                                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '10px', alignItems: 'flex-end' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '12px' }}>Weight</label>
                                            <input
                                                type="number"
                                                value={variant.weight}
                                                onChange={(e) => handleVariantChange(index, 'weight', e.target.value)}
                                                step="0.1"
                                                min="0.1"
                                                required
                                                className="form-input"
                                                placeholder="e.g., 1"
                                            />
                                        </div>
                                        <div style={{ flex: 0.8 }}>
                                            <label style={{ fontSize: '12px' }}>Unit</label>
                                            <select
                                                value={variant.unit}
                                                onChange={(e) => handleVariantChange(index, 'unit', e.target.value)}
                                                required
                                                className="form-input"
                                            >
                                                <option value="gms">Grams</option>
                                                <option value="kg">Kg</option>
                                                <option value="pieces">Pieces</option>
                                            </select>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '12px' }}>Price (₹)</label>
                                            <input
                                                type="number"
                                                value={variant.price}
                                                onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                step="0.01"
                                                min="0.01"
                                                required
                                                className="form-input"
                                                placeholder="e.g., 100"
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ fontSize: '12px' }}>Stock</label>
                                            <input
                                                type="number"
                                                value={variant.quantity}
                                                onChange={(e) => handleVariantChange(index, 'quantity', e.target.value)}
                                                min="0"
                                                required
                                                className="form-input"
                                                placeholder="e.g., 50"
                                            />
                                        </div>
                                        {formData.variants.length > 1 && (
                                            <button 
                                                type="button" 
                                                onClick={() => removeVariant(index)}
                                                style={{ 
                                                    padding: '8px 12px', 
                                                    backgroundColor: '#dc3545', 
                                                    color: 'white', 
                                                    border: 'none', 
                                                    borderRadius: '4px', 
                                                    cursor: 'pointer',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-cancel" onClick={resetForm}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-submit">
                                    {editingProduct ? 'Update Product' : 'Add Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;
